import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './OnboardingChecklistWidget.css';

interface OnboardingStep {
    id: number;
    title: string;
    description: string;
    actionLabel?: string;
    actionUrl?: string;
    isExternal?: boolean;
}

const ONBOARDING_STEPS: OnboardingStep[] = [
    {
        id: 1,
        title: 'Şubenizi Ekleyin',
        description: 'Ayarlar bölümüne girin ve sol üstte bulunan "YENİ ŞUBE EKLE" butonu ile şubenizin ilini ekleyin.',
        actionLabel: 'Ayarlar\'a Git',
        actionUrl: '/settings'
    },
    {
        id: 2,
        title: 'Uzman, Hekim ve DSP Ekleyin',
        description: 'Üst menüdeki "UZMAN" butonu ile sisteminizde kayıtlı uzmanların bilgilerini girerek ekleyin. Aynı adımı HEKİM ve DSP için de yapın.',
        actionLabel: 'Uzmanlar\'a Git',
        actionUrl: '/experts'
    },
    {
        id: 3,
        title: 'Chrome Eklentisini Kurun',
        description: 'Sağ üstteki "EKLENTİ" butonu ile İSG Destek uzantısını Chrome\'a ekleyin. Bu eklenti, İSG-KÂTİP\'ten verileri çekmenizi sağlar.',
        actionLabel: 'Eklentiyi İndir',
        actionUrl: 'https://chromewebstore.google.com/detail/i%CC%87sg-destek/kmccocjabhgjidlmimobpeiadlgpaicd',
        isExternal: true
    },
    {
        id: 4,
        title: 'Çıkış Yapıp Tekrar Girin',
        description: 'Tüm yetki ve eklenti tanımlarının güncellenmesi için sistemden bir kere çıkış yapıp tekrar giriş yapın.',
        actionLabel: 'Çıkış Yap',
        actionUrl: '/login'
    },
    {
        id: 5,
        title: 'Şubenizi Seçin',
        description: 'Üst menüde bulunan "ŞUBELER" menüsünden eklemiş olduğunuz (kendinize ait) şubenizi seçin. Bu şube otomatik olarak her seferinde orada seçili kalacaktır.',
    },
    {
        id: 6,
        title: 'İSG-KÂTİP\'ten Verileri Çekin',
        description: 'Bu sekme açıkken, diğer bir sekmeden isgkatip.csgb.gov.tr adresine girin. Kurumsal menüden firmanızı seçip Sözleşmeler menüsünde bekleyin. Sağ üstte Eklentinin AKTİF olduğunu görünce "VERİLERİ ÇEK" butonu ile tüm bilgileri sisteme aktarın.',
        actionLabel: 'İSG-KÂTİP\'i Aç',
        actionUrl: 'https://isgkatip.csgb.gov.tr',
        isExternal: true
    },
    {
        id: 7,
        title: 'İşyerlerinizi İnceleyin',
        description: 'Birkaç dakika sonra sisteme tüm verilerin geldiğini ve üst menüdeki "İşyerleri" sayfasında uzman/hekim atamalarını göreceksiniz. İstediğiniz işyerine tıklayarak kartını inceleyebilirsiniz.',
        actionLabel: 'İşyerleri\'ne Git',
        actionUrl: '/workplaces'
    }
];

const TOTAL_SETUP_STEPS = ONBOARDING_STEPS.length; // 7

// 4 Kasım 2026 00:00 UTC ve sonrası (tüm gün içinde kayıt olanlar)
const FEATURE_LAUNCH_TIMESTAMP = new Date('2026-11-04T00:00:00Z').getTime();

export const OnboardingChecklistWidget: React.FC = () => {
    const { user, isAuthenticated, logout } = useAuth();
    const navigate = useNavigate();

    // Sadece admin, osgb_admin ve hr rolleri için geçerli
    const isAllowedRole = user?.role === 'admin' || user?.role === 'osgb_admin' || user?.role === 'hr';

    const userId = user?.id || 'guest';
    const storageKeySteps = `isg_onboarding_steps_${userId}`;
    const storageKeyMinimized = `isg_onboarding_minimized_${userId}`;
    const storageKeyFinished = `isg_onboarding_finished_${userId}`;

    // State: Kullanıcının işaretlediği adımlar
    const [completedSteps, setCompletedSteps] = useState<number[]>(() => {
        try {
            const saved = localStorage.getItem(storageKeySteps);
            return saved ? JSON.parse(saved) : [];
        } catch {
            return [];
        }
    });

    // Simge durumuna küçültülmüş mü?
    // Her yeni girişte açık başlar, kullanıcı unutmasın
    const [isMinimized, setIsMinimized] = useState<boolean>(false);

    // Kullanıcı tüm adımları %100 bitirip kendi isteğiyle kapattı mı?
    const [isFinished, setIsFinished] = useState<boolean>(() => {
        try {
            return localStorage.getItem(storageKeyFinished) === 'true';
        } catch {
            return false;
        }
    });

    // LocalStorage senkronizasyonu
    useEffect(() => {
        try {
            localStorage.setItem(storageKeySteps, JSON.stringify(completedSteps));
        } catch (e) {
            console.error('Failed to save onboarding steps', e);
        }
    }, [completedSteps, storageKeySteps]);

    // Kullanıcı adımı işaretleyince
    const toggleStep = (stepId: number) => {
        setCompletedSteps(prev => {
            const next = prev.includes(stepId) ? prev.filter(id => id !== stepId) : [...prev, stepId];
            return next;
        });
    };

    const handleActionClick = (step: OnboardingStep) => {
        if (!step.actionUrl) return;

        if (step.id === 4) {
            // 4. Adım Çıkış Yap: Adım 4'ü de otomatik tamamlandı işaretleyelim
            if (!completedSteps.includes(4)) {
                const updated = [...completedSteps, 4];
                try {
                    localStorage.setItem(storageKeySteps, JSON.stringify(updated));
                } catch {}
            }
            if (logout) logout();
            navigate('/login');
            return;
        }

        if (step.isExternal) {
            window.open(step.actionUrl, '_blank', 'noopener,noreferrer');
        } else {
            navigate(step.actionUrl);
        }
    };

    const handleToggleMinimize = (min: boolean) => {
        setIsMinimized(min);
        try {
            // Oturumlar arasi gizli kalmasin diye kaydetmiyoruz
        } catch {}
    };

    const handleFinishAndClose = () => {
        setIsFinished(true);
        try {
            localStorage.setItem(storageKeyFinished, 'true');
        } catch {}
    };

    // ESKİ AKTİF KULLANICILAR İLE YENİ KULLANICILARIN AYRIMI:
    // 1. Kullanıcı daha önce süreci %100 bitirip kapattıysa gösterme
    if (isFinished) {
        return null;
    }

    // 2. Kullanıcı oturum açmamışsa veya yetkisiz rol ise gösterme
    if (!isAuthenticated || !isAllowedRole) {
        return null;
    }

    // 3. Eski kullanıcı kontrolü:
    // Gün içinde (4 Kasım) kayıt olanlar veya Ömer Doğancı (antisgder) için HER ZAMAN göster!
    const userCreatedAt = user?.createdAt ? new Date(user.createdAt).getTime() : 0;
    const isTargetUser = Boolean(
        (user as any)?.email?.toLowerCase()?.includes('antisgder') || 
        user?.username?.toLowerCase()?.includes('antisgder')
    );
    const isRegisteredAfterLaunch = userCreatedAt >= FEATURE_LAUNCH_TIMESTAMP || isTargetUser;
    const hasStartedOnboarding = completedSteps.length > 0;

    // Eğer eski bir kullanıcıysa ve henüz onboarding başlatmamışsa gösterme
    if (!isRegisteredAfterLaunch && !hasStartedOnboarding && userCreatedAt > 0) {
        return null;
    }

    const completedCount = completedSteps.length;
    const progressPercent = Math.round((completedCount / TOTAL_SETUP_STEPS) * 100);
    const allCompleted = completedCount >= TOTAL_SETUP_STEPS;

    // Eğer simge durumuna küçültülmüşse sağ altta rozet butonu olarak kalsın (ASLA KAYBOLMAZ)
    if (isMinimized) {
        return (
            <button
                className="onboarding-widget-floating-trigger"
                onClick={() => handleToggleMinimize(false)}
                title="Hızlı Başlangıç Rehberi (%100 tamamlanana kadar açık kalır)"
            >
                <i className="fas fa-rocket"></i>
                <span>Başlangıç Rehberi</span>
                <span className="onboarding-widget-progress-badge">
                    {allCompleted ? '✓ %100' : `%${progressPercent}`}
                </span>
            </button>
        );
    }

    return (
        <div className="onboarding-widget-panel">
            {/* Başlık Şeridi */}
            <div className="onboarding-widget-header">
                <div className="onboarding-widget-header-title">
                    <i className="fas fa-compass"></i>
                    <span>Hızlı Başlangıç Rehberi</span>
                </div>
                <div className="onboarding-widget-header-actions">
                    <button
                        className="onboarding-widget-icon-btn"
                        onClick={() => handleToggleMinimize(true)}
                        title="Simge Durumuna Küçült (İstediğinizde sağ alttan açabilirsiniz)"
                    >
                        <i className="fas fa-minus"></i>
                    </button>
                </div>
            </div>

            {/* İlerleme Çubuğu */}
            <div className="onboarding-widget-progress-section">
                <div className="onboarding-widget-progress-labels">
                    <span>Kurulum İlerlemesi</span>
                    <span style={{ fontWeight: 700, color: allCompleted ? '#166534' : '#0057D9' }}>
                        %{progressPercent} ({completedCount}/{TOTAL_SETUP_STEPS})
                    </span>
                </div>
                <div className="onboarding-widget-progress-bar-bg">
                    <div
                        className="onboarding-widget-progress-bar-fill"
                        style={{ width: `${progressPercent}%` }}
                    />
                </div>
            </div>

            {/* Adımlar Gövdesi */}
            <div className="onboarding-widget-body">
                {!allCompleted && (
                    <div style={{ background: '#f8fafc', padding: '8px 12px', borderRadius: '8px', fontSize: '12px', color: '#475569', border: '1px dashed #cbd5e1' }}>
                        <i className="fas fa-info-circle" style={{ color: '#0057D9', marginRight: '6px' }}></i>
                        Kurulum <strong>%100 tamamlanana kadar</strong> kaç kere çıkıp girseniz de bu rehber kaldığınız yerden sizi yönlendirmeye devam edecektir.
                    </div>
                )}

                {/* 1'den 7'ye Kurulum Adımları */}
                {ONBOARDING_STEPS.map(step => {
                    const isDone = completedSteps.includes(step.id);
                    return (
                        <div
                            key={step.id}
                            className={`onboarding-step-item ${isDone ? 'is-completed' : ''}`}
                        >
                            <input
                                type="checkbox"
                                checked={isDone}
                                onChange={() => toggleStep(step.id)}
                                className="onboarding-step-checkbox"
                                id={`step-chk-${step.id}`}
                            />
                            <div className="onboarding-step-details">
                                <div className="onboarding-step-header">
                                    <span className="onboarding-step-number">{step.id}</span>
                                    <label
                                        htmlFor={`step-chk-${step.id}`}
                                        className="onboarding-step-title"
                                        style={{ cursor: 'pointer' }}
                                    >
                                        {step.title}
                                    </label>
                                </div>
                                <p className="onboarding-step-desc">{step.description}</p>
                                {step.actionLabel && step.actionUrl && (
                                    <button
                                        type="button"
                                        onClick={() => handleActionClick(step)}
                                        className="onboarding-step-action-btn"
                                    >
                                        <span>{step.actionLabel}</span>
                                        <i className={step.isExternal ? 'fas fa-external-link-alt' : 'fas fa-arrow-right'} style={{ fontSize: '10px' }}></i>
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}

                {/* 8. Adım Bilgi Kartı: Saha Mobil Girişi */}
                <div className="onboarding-info-card" style={{ borderLeft: '4px solid #0057D9' }}>
                    <div className="onboarding-info-card-title">
                        <i className="fas fa-mobile-alt" style={{ color: '#0057D9' }}></i>
                        <span>8. Sahada Mobil Kullanım</span>
                    </div>
                    <p className="onboarding-info-card-text">
                        Saha personeli telefondan <a href="/field/login" target="_blank" rel="noopener noreferrer" className="onboarding-info-link">isgdestek.com.tr/field/login</a> adresinden;<br />
                        • <strong>OSGB Adı:</strong> Sisteme kayıtlı adınız<br />
                        • <strong>Kullanıcı Adı:</strong> Personelin cep telefonu no<br />
                        • <strong>Şifre:</strong> Cep telefonunun son 4 hanesi ile giriş yapabilir.
                    </p>
                </div>

                {/* 9. Adım Bilgi Kartı: Uzman / Hekim Web Girişi */}
                <div className="onboarding-info-card" style={{ borderLeft: '4px solid #0FA968' }}>
                    <div className="onboarding-info-card-title">
                        <i className="fas fa-desktop" style={{ color: '#0FA968' }}></i>
                        <span>9. Uzman / Hekim Web Girişi</span>
                    </div>
                    <p className="onboarding-info-card-text">
                        Personeller bilgisayardan kişisel panellerine <a href="/web/field/login" target="_blank" rel="noopener noreferrer" className="onboarding-info-link">isgdestek.com.tr/web/field/login</a> adresinden ulaşabilirler.
                    </p>
                </div>

                {/* 7 Adım Tamamlandıysa (%100) Kutlama ve Kapatma Kartı En Altta */}
                {allCompleted && (
                    <div className="onboarding-celebration-card" style={{ marginTop: '10px' }}>
                        <div className="onboarding-celebration-title">
                            🎉 ARTIK BAŞLAYABİLİRİZ!
                        </div>
                        <p className="onboarding-celebration-desc" style={{ marginBottom: '12px' }}>
                            Tüm kurulum adımlarını başarıyla tamamladınız. Sisteme tamamen hazırsınız!
                        </p>
                        <button
                            type="button"
                            onClick={handleFinishAndClose}
                            style={{
                                background: '#166534',
                                color: '#ffffff',
                                border: 'none',
                                padding: '10px 16px',
                                borderRadius: '8px',
                                fontSize: '13px',
                                fontWeight: 700,
                                cursor: 'pointer',
                                width: '100%'
                            }}
                        >
                            ✓ Kurulumu Tamamla ve Kılavuzu Kapat
                        </button>
                    </div>
                )}
            </div>

            {/* Alt Çubuk (WhatsApp Canlı Destek) */}
            <div className="onboarding-widget-footer">
                <a
                    href="https://wa.me/905072136474?text=Merhaba,%20ISGDestek%20kurulum%20ad%C4%B1mlar%C4%B1nda%20deste%C4%9Fe%20ihtiyac%C4%B1m%20var,%20yard%C4%B1mc%C4%B1%20olur%20musunuz?"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="onboarding-whatsapp-btn"
                >
                    <i className="fab fa-whatsapp" style={{ fontSize: '17px' }}></i>
                    <span>Canlı Destek: 0507 213 64 74</span>
                </a>
            </div>
        </div>
    );
};

export default OnboardingChecklistWidget;
